import type { State } from "../State";
import type { PdfChapterConfig } from "../pdf/PdfExportConfig";
import { collectTechnicalReport } from "../pdf/technicalReports/collect";
import { isTechnicalReportId } from "../pdf/technicalReports/catalog";
import { collectBillOfMaterials, materialChapter, materialChapterOrder } from "../pdf/billOfMaterialsReport";
import { collectGroupAddressReport } from "../pdf/groupAddressReport";
import { collectFuseReport } from "../pdf/fuseReport";
import { collectClassicRoombook } from "../pdf/classicRoombookReport";
import { comparePdfLabels, pdfHeaderText } from "../pdf/pdfTagName";
import { specialChannelTypes } from "../models/ChannelType";
import { TAG_TEMPERATUR } from "../models/Constants";
import type { TechnicalReport } from "../pdf/technicalReports/types";

export type ExcelValue = string | number;
export type ExcelTable = { title?: string; parentGroupTitle?: string; groupTitle?: string; columns: string[]; rows: ExcelValue[][]; rowLevels?: number[];
  emphasizedTitle?: boolean; stripedRows?: boolean;
  hideHeader?: boolean; frameHeader?: boolean;
  rowGroupStarts?: number[]; blockEndRows?: boolean[]; terminalTagColumn?: number;
  image?: { bytes: Uint8Array; width: number; height: number; column: number; lastColumn?: number };
  headerMerges?: Array<[number, number]>;
  topAlignedRows?: boolean[];
  plainRows?: boolean[];
  rowLinks?: Array<{ column: number; target: string }>;
  rowMerges?: Array<Array<[number, number]>>; smallTextRows?: boolean[]; licenseNoticeRows?: boolean[] };
export type ExcelSection = { title: string; contentsPath?: Array<{ title: string; key: string }>;
  details?: { label: string; value: string }[]; tables: ExcelTable[]; notes?: string[] };
export type ExcelReport = { sections: ExcelSection[]; landscape?: boolean; columnWidths?: number[];
  noWrapColumns?: number[]; hideSectionHeadings?: boolean; textFormat?: boolean;
  headerMerges?: Array<[number, number]>; leftAlignedHeaders?: number[]; noWrapHeaders?: number[];
  outlineHierarchy?: boolean; pageBreakBeforeLevel?: number; integerColumns?: number[]; secondaryHeader?: string[];
  secondaryHeaderMerges?: Array<[number, number]>; documentHeader?: boolean; documentTitleColumn?: number; removeHeaderSpacer?: boolean; frameHeaders?: boolean; fitPageWidth?: boolean };

function integerValue(value: string): ExcelValue {
  const number = Number(value);
  return /^\d+$/.test(value.trim()) && Number.isSafeInteger(number) ? number : value;
}

function contentsLabel(tag: string | undefined, name: string | undefined): string {
  return [tag, name].filter(Boolean).join(" · ");
}

/** Converts only explicit quantitative columns; addresses, tags and product numbers stay text. */
function technicalValues(report: TechnicalReport): ExcelReport {
  return { ...report, sections: report.sections.map(section => ({ ...section, tables: section.tables.map(table => ({
    ...table, rows: table.rows.map(row => row.map((value, index) => {
      const numeric = /^(Anzahl|Menge|Stückzahl|Preis|Einzelpreis|Gesamtpreis|Summe|TE|Busstrom|Verwendungen)(\b|$)/i.test(table.columns[index]);
      if (!numeric) return value;
      const money = /preis/i.test(table.columns[index]);
      const normalized = value.trim().replace(/\s*(€|mA|TE)$/, "").trim();
      const number = money ? normalized.replace(/\./g, "").replace(",", ".") : normalized.replace(",", ".");
      return /^-?\d+(?:\.\d+)?$/.test(number) ? Number(number) : value;
    })) })) })) };
}

/** Apply separator formatting to every list before the writer measures widths and row heights. */
export async function collectExcelReport(app: State, chapter: PdfChapterConfig): Promise<ExcelReport> {
  const report = await collectExcelReportData(app, chapter);
  return { ...report, sections: report.sections.map(section => ({ ...section,
    details: section.details?.map(detail => ({ ...detail, value: detail.value.replace(/\s*\|\s*/g, "\n") })),
    tables: section.tables.map(table => ({ ...table, rows: table.rows.map(row => row.map((value, index) =>
      typeof value === "string" ? value.replace(/\s*\|\s*/g, report.noWrapColumns?.includes(index) ? " · " : "\n") : value)) })),
  })) };
}

async function collectExcelReportData(app: State, chapter: PdfChapterConfig): Promise<ExcelReport> {
  if (isTechnicalReportId(chapter.id)) {
    if (chapter.id === "actorList") {
      const { loadDeviceFrontImage } = await import("../pdf/loadDeviceFrontImage");
      const devices = [...app.allActors(), ...app.allButtons().filter(button => button.channels.asArray().some(channel => channel.isActorChannel))];
      for (const device of [...new Map(devices.map(device => [device.id, device])).values()]) await loadDeviceFrontImage(device);
    }
    const sourceReport = collectTechnicalReport(app, chapter.id, chapter.id === "actorList" ? { ...chapter, actorShowImage: true } : chapter);
    const report = technicalValues(sourceReport);
    if (chapter.id === "actorList") {
      const { loadExcelFrontImage } = await import("./loadExcelDocumentLogo");
      const sections: ExcelSection[] = [];
      for (const [index, section] of report.sections.entries()) {
        const source = sourceReport.sections[index].frontImage;
        const image = source ? await loadExcelFrontImage(source) : undefined;
        const details = section.details ?? [];
        sections.push({ ...section, details: undefined, tables: [{
          columns: ["", "Name", "Wert", "", ""], hideHeader: true, stripedRows: false,
          rows: details.map(detail => ["", detail.label, detail.value.replace(/\s*\|\s*/g, "\n"), "", ""]),
          topAlignedRows: details.map(() => true),
          image: image ? { ...image, column: 3, lastColumn: 4 } : undefined,
        }, ...section.tables.map(table => ({ ...table, title: "Kanäle", emphasizedTitle: true,
          frameHeader: true, stripedRows: false, columns: ["", ...table.columns],
          rows: table.rows.map(row => ["", ...row]),
        }))] });
      }
      return { sections, landscape: false, removeHeaderSpacer: true, columnWidths: [4, 22, 34, 14, 14] };
    }
    if (chapter.id !== "terminals") return report;
    const tables = report.sections.flatMap(section => section.tables);
    const count = Math.max(3, ...tables.map(table => table.columns.length));
    const singleLine = (value: ExcelValue): ExcelValue => typeof value === "string" ? value.replace(/\r?\n/g, " · ") : value;
    const widths = Array.from({ length: count }, (_, index) => Math.min(255, Math.max(3, ...tables.flatMap(table =>
      [table.columns[index] ?? "", ...table.rows.map(row => singleLine(row[index] ?? ""))]
        .map(value => Math.ceil(String(value).length * 1.15) + 2)))));
    return { ...report, fitPageWidth: true, columnWidths: widths,
      noWrapColumns: Array.from({ length: count }, (_, index) => index),
      noWrapHeaders: Array.from({ length: count }, (_, index) => index),
      sections: report.sections.map(section => ({ ...section, tables: section.tables.map(table => ({ ...table,
        rows: table.rows.map(row => row.map(singleLine)),
        stripedRows: false, terminalTagColumn: 2,
        blockEndRows: table.rows.map((_, index) => index === table.rows.length - 1 || (table.rowGroupStarts ?? []).includes(index + 1)),
      })) })),
    };
  }
  if (chapter.id === "billOfMaterials") {
    const scopes = collectBillOfMaterials(app, { mode: chapter.billOfMaterialsMode ?? "total",
      usage: chapter.billOfMaterialsUsage ?? false, distinguishLightTypes: chapter.billOfMaterialsLightTypes ?? false,
      includeWithoutProducts: chapter.billOfMaterialsWithoutProducts ?? true });
    const sections = scopes.flatMap(scope => materialChapterOrder.flatMap(category => {
      const positions = scope.positions.filter(position => materialChapter(position.section) === category);
      if (!positions.length) return [];
      const tables: ExcelTable[] = [{ columns: ["Bereich", "Gruppe", "Spezifikation", "Anzahl"],
        rows: positions.map(p => [p.section, p.group, p.specification, p.count]) }];
      if (chapter.billOfMaterialsUsage && !(chapter.billOfMaterialsMode === "floors" && scope.isTotal)) tables.push({
        title: "Verwendungen", columns: ["Spezifikation", "Tag", "Etage", "Raum", "Name"],
        rows: positions.flatMap(p => p.usages.map(u => [p.specification, u.tag, u.floor, u.room, u.name])) });
      return [{ title: `${scope.title} · ${category}`, tables }];
    }));
    if (chapter.billOfMaterialsMode !== "floors") sections.sort((a, b) =>
      materialChapterOrder.findIndex(c => a.title.endsWith(c)) - materialChapterOrder.findIndex(c => b.title.endsWith(c)));
    return { sections };
  }
  if (chapter.id === "groupAddresses") {
    const report = collectGroupAddressReport(app.addresses, chapter.detailedGroupAddressUsage ?? false, true);
    const mains = Array.from(app.addresses).sort((a, b) => a.fullAddress.localeCompare(b.fullAddress, "de", { numeric: true }));
    const sections = report.sections.map((section, index) => {
      const main = mains[index];
      const rows: ExcelValue[][] = [[integerValue(main.fullAddress), "", "", main.name, "", "", "", "", ""]];
      const rowLevels = [1];
      const rowMerges: Array<Array<[number, number]>> = [[[3, 7]]];
      const smallTextRows = [false];
      for (const row of section.rows) {
        rows.push(["", row.level === 2 ? row.address : "", row.level === 3 ? row.address : "", row.name, "", "", "", "", row.level === 3 ? row.usages : ""]);
        rowLevels.push(row.level);
        rowMerges.push([[3, 7]]);
        smallTextRows.push(false);
        for (const usage of row.usageDetails ?? []) {
          rows.push(["", "", "", "", usage.ancestorTag, usage.channelLabel ?? usage.channelDescription, usage.channelName ?? usage.ancestorName, integerValue(usage.signalNumber), ""]);
          rowLevels.push(3);
          rowMerges.push([]);
          smallTextRows.push(true);
        }
      }
      return { title: section.title, tables: [{ columns: ["Level 1", "Level 2", "Level 3", "Name", "", "", "", "", "Anzahl Verwendungen"], rows, rowLevels, rowMerges, smallTextRows }] };
    });
    const longestName = sections.reduce((longest, section) => section.tables[0].rows.reduce((length, row) =>
      Math.max(length, String(row[3]).length), longest), 30);
    const usageWidths = [4, 5, 6, 7].map((index, i) => Math.min(255, sections.reduce((width, section) =>
      section.tables[0].rows.reduce((value, row) => Math.max(value, Math.ceil(String(row[index]).length * 1.15) + 2), width), [12, 17, 23, 8][i])));
    const nameWidth = Math.min(255, Math.ceil(longestName * 1.15) + 4);
    usageWidths[2] = Math.max(usageWidths[2], nameWidth - 5 - usageWidths[0] - usageWidths[1] - usageWidths[3]);
    return { sections, landscape: false, hideSectionHeadings: true, noWrapColumns: [3, 4, 5, 6, 7], textFormat: true, documentHeader: true, removeHeaderSpacer: true, frameHeaders: true,
      headerMerges: [[3, 7]], leftAlignedHeaders: [3], noWrapHeaders: [8], outlineHierarchy: true, pageBreakBeforeLevel: 1, integerColumns: [0, 7, 8],
      secondaryHeader: chapter.detailedGroupAddressUsage ? ["", "", "", "", "Verwendung", "", "", "Signalnummer", ""] : undefined,
      secondaryHeaderMerges: [[7, 8]],
      columnWidths: [7, 9, 10, 5, ...usageWidths, 24] };
  }
  if (chapter.id === "fuseList") {
    const sections = collectFuseReport(app.cabinets.asArray(), chapter.fuseSort).sections.map(section => {
      const rows: ExcelValue[][] = [[section.fi, "", [section.cabinetTitle, section.fiTitle ?? section.title].filter(Boolean).join(" • "), "", "", "", ""]];
      const rowLevels = [1];
      const rowMerges: Array<Array<[number, number]>> = [[[2, 6]]];
      for (const circuit of section.circuits) {
        rows.push(["", circuit.tag, circuit.name, "", "", "", ""]);
        rowLevels.push(2); rowMerges.push([[2, 6]]);
        for (const consumer of circuit.consumers) {
          rows.push(["", "", "", consumer.tag, consumer.name, consumer.channel, consumer.terminal]);
          rowLevels.push(3); rowMerges.push([]);
        }
      }
      return { title: section.title, tables: [{ columns: ["FI", "Sicherung", "", "Verbraucher", "Name", "Aktorkanal", "Klemme"], rows, rowLevels, rowMerges }] };
    });
    return { sections, landscape: false, hideSectionHeadings: true, outlineHierarchy: true,
      columnWidths: [7, 10, 4, 20, 20, 12, 10], frameHeaders: true };
  }
  if (chapter.id === "roombook") {
    const c1 = app.projectMetadata.isC1Project
      ? (await import("../../ConceptOne/src/raumbuch/C1_Raumbuch")).collectC1RoombookForExport(chapter.roombookMode ?? "functions")
      : undefined;
    const rooms = c1?.rooms ?? collectClassicRoombook(app);
    const sections: ExcelSection[] = rooms.map(room => {
      const title = `${room.fullTag ?? ""} • ${room.floorName ?? room.floorLabel} • ${room.roomName ?? room.roomLabel}`;
      const rows: ExcelValue[][] = [[title, ...Array<string>(8).fill("")]];
      const rowLevels = [1];
      const topAlignedRows = [false];
      const rowMerges: Array<Array<[number, number]>> = [[[0, 8]]];
      for (const category of room.categories) {
        const depth = category.level ?? 0;
        const column = depth ? 2 : 1;
        const categoryRow = Array<string>(9).fill("");
        categoryRow[column] = [category.title, category.details?.replace(/\s*\|\s*/g, "\n")].filter(Boolean).join(" · ");
        rows.push(categoryRow); rowLevels.push(Math.min(7, 2 + depth)); rowMerges.push([[column, 8]]); topAlignedRows.push(false);
        for (const item of category.items) {
          rows.push(["", "", item.name, item.tag, item.details.replace(/\s*\|\s*/g, "\n"), "", "", "", ""]);
          rowLevels.push(Math.min(8, 3 + depth)); rowMerges.push([[4, 8]]); topAlignedRows.push(true);
        }
      }
      if (room.notes?.length) {
        rows.push(["", "Notizen", "", "", "", "", "", "", ""]); rowLevels.push(2); rowMerges.push([[1, 8]]); topAlignedRows.push(false);
        for (const note of room.notes) {
          rows.push(["", "", note, "", "", "", "", "", ""]); rowLevels.push(3); rowMerges.push([[2, 8]]); topAlignedRows.push(false);
        }
      }
      return { title, contentsPath: [
        { title: contentsLabel(room.floorTag, room.floorName) || room.floorLabel, key: room.floorLabel },
        { title: contentsLabel(room.roomTag, room.roomName) || room.roomLabel, key: room.id },
      ], tables: [{ columns: ["Raum", "", "Name", "Tag", "Details", "", "", "", ""], rows, rowLevels, rowMerges, topAlignedRows,
        headerMerges: [[0, 1], [4, 8]] }] };
    });
    if (c1) {
      const { visibleC1RoombookSummaryColumns, getC1RoombookWiringCountExplanation } = await import("../../ConceptOne/src/raumbuch/C1_RaumbuchSummary");
      const columns = visibleC1RoombookSummaryColumns(c1.summary);
      const summaryRows = [
        ...c1.summary.floors.flatMap(floor => floor.rows.map(room => ({ floor: floor.label, room }))),
        { floor: "Gesamt", room: c1.summary.total },
      ];
      sections.push({ title: "Projektübersicht", notes: [getC1RoombookWiringCountExplanation()], tables: [{
        columns: ["Etage", "Raum", "Kategorie", "Anzahl", "Gruppen / einzeln geschaltet", "", "", "", ""],
        headerMerges: [[4, 8]],
        rows: [["Projektübersicht", "", "", "", "", "", "", "", ""], ...summaryRows.flatMap(({ floor, room }) => columns.filter(column => room.values[column.id] > 0)
          .map(column => [floor, room.label, `${column.group} · ${column.label}`, room.values[column.id], room.independentlyWiredValues[column.id], "", "", "", ""]))],
        rowLevels: [1, ...summaryRows.flatMap(({ room }) => columns.filter(column => room.values[column.id] > 0).map(() => 3))],
        rowMerges: [[[0, 8]], ...summaryRows.flatMap(({ room }) => columns.filter(column => room.values[column.id] > 0).map(() => [[4, 8]] as Array<[number, number]>))],
      }] });
    }
    return { sections, hideSectionHeadings: true, outlineHierarchy: true, headerMerges: [[0, 1], [4, 8]], documentHeader: true, documentTitleColumn: 3,
      columnWidths: [5, 7, 34, 12, 6, 6, 11, 11, 11], landscape: false };
  }
  if (chapter.id === "buttonList") {
    const { buttonListSpecification } = await import("../../ConceptOne/src/plan/C1_ButtonListPdf");
    const { loadDeviceFrontImage } = await import("../pdf/loadDeviceFrontImage");
    const { loadExcelFrontImage } = await import("./loadExcelDocumentLogo");
    const buttons = [...new Map(app.allButtons().filter(b => !b.isSensor && !b.isDecentralActor).map(b => [b.id, b])).values()];
    buttons.sort((a, b) => comparePdfLabels(a.room?.parentFloor, b.room?.parentFloor)
      || comparePdfLabels(a.room, b.room) || comparePdfLabels(a, b));
    const sections: ExcelSection[] = [];
    for (const button of buttons) {
      const source = await loadDeviceFrontImage(button);
      const image = source ? await loadExcelFrontImage(source) : undefined;
      const specifications = buttonListSpecification(button);
      sections.push({
      title: `${button.fullTag} • ${button.room?.parentFloor?.name ?? ""} • ${button.room?.name ?? ""} • ${button.name}`,
      contentsPath: [
        { title: contentsLabel(button.room?.parentFloor?.tag, button.room?.parentFloor?.name) || "Ohne Etage", key: button.room?.parentFloor?.id ?? pdfHeaderText(button.room?.parentFloor) },
        { title: contentsLabel(button.room?.tag, button.room?.name) || "Ohne Raum", key: button.room?.id ?? pdfHeaderText(button.room) },
        { title: contentsLabel(button.tag, button.name), key: button.id },
      ],
      tables: [{ columns: ["", "Name", "Wert", ""], hideHeader: true, stripedRows: false,
        rows: specifications.map(([label, value]) => ["", label, value.replace(/\s*\|\s*/g, "\n"), ""]),
        topAlignedRows: specifications.map(() => true),
        image: image ? { ...image, column: 3 } : undefined,
      }, { title: "Tastenbelegung", emphasizedTitle: true, stripedRows: false, frameHeader: true,
        columns: ["", "Taste / Funktion", "Beschriftung", "Zugeordnete Verbraucher"],
        rows: button.buttonChannels.asArray().filter(channel => !specialChannelTypes.includes(channel.channelType)
          && [channel.tag, channel.mappedChannel?.tag].every(tag => !tag?.toUpperCase().startsWith("TX")
            || tag.toUpperCase() === TAG_TEMPERATUR || tag.toUpperCase() === "TX__T"))
          .sort((a, b) => a.tag.localeCompare(b.tag, app.settings.uiLanguage, { numeric: true })).map(channel => {
            const partners = new Map<string, string>();
            for (const connection of channel.connections.asArray()) {
              const partner = connection.getPartner(channel);
              if (partner) partners.set(partner.id, pdfHeaderText(partner));
            }
            return ["", `${channel.mappedChannel?.name || channel.tag}\n${app.translate(`channelType.${channel.channelType}`, channel.channelType)}`,
              channel.nameOrConnectedNames || "–", [...partners.values()].join("\n") || "–"];
          }) }] });
    }
    return { sections, columnWidths: [4, 24, 32, 30], landscape: false };
  }
  throw new Error(`Kein Excel-Listendokument für ${chapter.id}.`);
}
